import { escapeHtml } from '../lib/html.js';
import { statusPillClass } from '../lib/status.js';
import { G2_ICONS } from '../data/icons.js';
import { FACEBOOK_MARK } from '../data/sections.js';
import { buildHref } from '../lib/router.js';
import { paginate, LIST_PAGE_SIZE } from '../lib/pagination.js';
import { renderPager } from './pager.js';

const pendingSync = new Set();

export function markBmSyncPending(id) {
  pendingSync.add(String(id));
}

export function clearBmSyncPending(id) {
  pendingSync.delete(String(id));
}

export function isBmSyncBusy(bm) {
  if (!bm) return false;
  if (pendingSync.has(String(bm.id))) return true;
  return bm.sync_status === 'syncing';
}

function fmtWhen(iso) {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function syncLabel(status) {
  const map = {
    idle: 'parada',
    syncing: 'sincronizando',
    done: 'pronta',
    error: 'erro',
  };
  return map[status] || status || 'manual';
}

function verificationLabel(status) {
  const map = {
    verified: 'verificada',
    not_verified: 'não verificada',
    pending: 'em revisão',
  };
  return map[String(status || '').toLowerCase()] || '';
}

function teaserHtml() {
  return `
    <div class="wizard-teaser">
      <div>
        <h3>Conectar a sua BM</h3>
        <p>Entre com o Facebook que administra a BM. O Meta pede permissão e as contas dessa conta entram na Torre.</p>
      </div>
      <button type="button" class="btn btn-primary" data-action="bm-wizard">Conectar Facebook</button>
    </div>`;
}

export function renderBms({ bms = [], contas = [], page = 1 } = {}) {
  const pg = paginate(bms, page, LIST_PAGE_SIZE);
  const ativas = contas.filter((c) => c.status === 'ativa').length;
  const cards = pg.total
    ? pg.items.map((bm) => {
        const nContas = contas.filter((c) => c.bmId === bm.id).length;
        const nAtivas = contas.filter((c) => c.bmId === bm.id && c.status === 'ativa').length;
        const busy = isBmSyncBusy(bm);
        const facebook = Boolean(bm.facebookId);
        const sync = facebook ? (bm.sync_status || 'idle') : 'manual';
        const verified = verificationLabel(bm.verificationStatus);
        const footHint = bm.sync_error
          ? escapeHtml(bm.sync_error)
          : bm.last_sync_at
            ? `Sincronizada ${escapeHtml(fmtWhen(bm.last_sync_at))}`
            : facebook
              ? 'Ainda não sincronizou'
              : 'Cadastrada à mão — conecte o Facebook para importar contas';
        return `<article class="g-card store-card">
          <div class="store-card-top">
            <div class="store-card-id">
              <span class="platform-logo meta" title="Meta">${FACEBOOK_MARK}</span>
              <div class="store-card-copy">
                <div class="cell-title"><span class="store-card-name">${escapeHtml(bm.nome)}</span></div>
                <div class="cell-sub" title="${escapeHtml(bm.facebookId || bm.observacao || '')}">${escapeHtml(bm.facebookId ? `ID ${bm.facebookId}` : (bm.observacao || bm.plataforma || 'Meta'))}</div>
              </div>
            </div>
            <div class="row-actions">
              ${facebook ? `<button type="button" class="icon-btn icon-only${busy ? ' spinning' : ''}" data-action="sync" data-type="bms" data-id="${escapeHtml(bm.id)}" ${busy ? 'disabled' : ''} title="${busy ? 'Sincronizando…' : 'Sincronizar'}" aria-label="${busy ? 'Sincronizando' : 'Sincronizar'}">${busy ? G2_ICONS.spin : G2_ICONS.refresh}</button>` : ''}
              <button type="button" class="icon-btn icon-only" data-action="edit" data-type="bms" data-id="${escapeHtml(bm.id)}" title="Editar" aria-label="Editar">${G2_ICONS.edit}</button>
              <button type="button" class="icon-btn icon-only danger" data-action="delete" data-type="bms" data-id="${escapeHtml(bm.id)}" title="Excluir" aria-label="Excluir">${G2_ICONS.trash}</button>
            </div>
          </div>
          <div class="store-card-pills">
            <span class="platform-chip meta">${FACEBOOK_MARK}<span>${escapeHtml(bm.plataforma || 'Meta')}</span></span>
            <span class="pill ${statusPillClass(bm.status)}">${escapeHtml(bm.status || 'ativo')}</span>
            ${verified ? `<span class="pill ${statusPillClass(verified === 'verificada' ? 'ativo' : 'em revisão')}">${escapeHtml(verified)}</span>` : ''}
            <span class="pill ${statusPillClass(sync)}">${escapeHtml(syncLabel(sync))}</span>
          </div>
          <div class="store-card-metrics">
            <div class="store-metric">
              <div class="lbl">Contas</div>
              <div class="val">${nContas || bm.contasCount || 0}</div>
            </div>
            <div class="store-metric">
              <div class="lbl">Ativas</div>
              <div class="val">${nAtivas}</div>
            </div>
          </div>
          <div class="store-card-foot">
            <p class="cell-sub">${footHint}</p>
          </div>
        </article>`;
      }).join('')
    : `<div class="g-card store-card store-card-empty">Nenhuma BM. Entre com o Facebook para importar as Business Managers dessa conta.</div>`;

  return `
    ${teaserHtml()}
    <div class="fat-stats" style="margin-bottom:16px;flex-wrap:wrap">
      <div class="mini-stat">
        <div class="lbl">BMs</div>
        <div class="val">${bms.length}</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Contas</div>
        <div class="val">${contas.length}</div>
        <div class="cell-sub">${ativas} ativa(s)</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Do Facebook</div>
        <div class="val">${bms.filter((b) => b.facebookId).length}</div>
      </div>
    </div>
    <div class="table-toolbar">
      <span class="cell-sub">${pg.total} BM(s)</span>
    </div>
    <div class="store-grid">${cards}</div>
    ${pg.total ? renderPager({
      id: 'bms',
      page: pg.page,
      totalPages: pg.totalPages,
      from: pg.from,
      to: pg.to,
      total: pg.total,
      label: 'BM(s)',
      hrefFor: (n) => buildHref({ section: 'bms', page: n }),
    }) : ''}
  `;
}
